import Alert from '@mui/material/Alert'
import Box from '@mui/material/Box'
import Grid from '@mui/material/Grid'
import Paper from '@mui/material/Paper'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableContainer from '@mui/material/TableContainer'
import TableHead from '@mui/material/TableHead'
import TableRow from '@mui/material/TableRow'
import Typography from '@mui/material/Typography'
import { useQuery } from '@tanstack/react-query'

import { getLedger, getWallet } from '../../api/portal'
import { can } from '../../auth/session'
import { usePortal } from '../../auth/sessionContext'
import { CopyableId } from '../../components/ui/CopyableId'
import { DataState } from '../../components/DataState'
import { UNSET, formatDateTime, formatIrr } from '../../format'
import { ledgerEntryLabel } from '../../vocabulary'

function Figure({
  label,
  amountIrr,
  hint,
  emphasis = false,
}: {
  label: string
  amountIrr: number
  hint: string
  emphasis?: boolean
}) {
  return (
    <Paper
      variant="outlined"
      sx={{
        p: 3,
        height: '100%',
        // The spendable balance is the figure that decides whether the next
        // call succeeds, so it is the only one on a tinted ground. Three
        // identical cards made a customer read all three to find the one that
        // answers their question.
        ...(emphasis ? { bgcolor: 'action.hover', borderColor: 'primary.light' } : {}),
      }}
    >
      <Typography variant="overline" color="text.secondary">
        {label}
      </Typography>
      <Typography
        component="p"
        sx={{
          my: 1,
          fontVariantNumeric: 'tabular-nums',
          fontWeight: emphasis ? 700 : 500,
          whiteSpace: 'nowrap',
          fontSize: emphasis ? 'clamp(1.6rem, 3.4vw, 2.4rem)' : 'clamp(1.1rem, 2vw, 1.5rem)',
          lineHeight: 1.3,
        }}
      >
        {formatIrr(amountIrr)}
      </Typography>
      <Typography variant="body2" color="text.secondary">
        {hint}
      </Typography>
    </Paper>
  )
}

/**
 * One movement's effect on the balance.
 *
 * Sign first, colour second. A ledger that separates money in from money out
 * by colour alone is unreadable to a colour-blind reader and invisible in a
 * printout, so the direction is stated in the glyph — «+» or «−» — and the
 * colour merely agrees with it.
 */
function Movement({ amountIrr }: { amountIrr: number }) {
  const outgoing = amountIrr < 0
  return (
    <Typography
      component="span"
      variant="body2"
      sx={{
        fontVariantNumeric: 'tabular-nums',
        fontWeight: 600,
        whiteSpace: 'nowrap',
        color: outgoing ? 'text.primary' : 'success.main',
      }}
    >
      {amountIrr > 0 ? '+\u200e' : ''}
      {formatIrr(amountIrr)}
    </Typography>
  )
}

export function WalletPage() {
  const { membership } = usePortal()
  const customerId = membership.customer.id
  const mayView = can(membership, 'view_wallet')

  const wallet = useQuery({
    queryKey: ['client', 'wallet', customerId],
    queryFn: ({ signal }) => getWallet(customerId, signal),
    enabled: mayView,
  })
  const ledger = useQuery({
    queryKey: ['client', 'ledger', customerId],
    queryFn: ({ signal }) => getLedger(customerId, 1, signal),
    enabled: can(membership, 'view_ledger'),
  })

  if (!mayView) {
    return (
      <Box>
        <Typography variant="h1" gutterBottom>
          کیف پول
        </Typography>
        <Typography variant="body2" color="text.secondary">
          نقش شما در این حساب دسترسی به اطلاعات مالی را شامل نمی‌شود. مالک، مدیر
          یا عضو مالی می‌تواند آن را ببیند.
        </Typography>
      </Box>
    )
  }

  return (
    <Box>
      <Typography variant="h1" gutterBottom>
        کیف پول
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        حساب شما پیش‌پرداخت است. بهای هر درخواست پیش از ارسال رزرو می‌شود و تنها در
        صورت موفقیت از حساب کسر می‌گردد؛ درخواست ناموفق هزینه‌ای ندارد. هر سطر گردش
        مالی، شناسهٔ پیگیری درخواستی را که باعث آن شده نشان می‌دهد تا بتوانید آن را با
        صفحهٔ «مصرف و درخواست‌ها» تطبیق دهید.
      </Typography>
      {/* There is no self-service top-up, so there is no button for one. A
          disabled «افزایش اعتبار» would look like a feature that is broken
          rather than one that is done by arrangement. */}
      <Alert severity="info" sx={{ mb: 3 }}>
        افزایش اعتبار از سمت ما ثبت می‌شود. برای واریز تازه با پشتیبانی تماس بگیرید؛
        پس از ثبت، مبلغ در همین صفحه و در گردش مالی زیر دیده می‌شود.
      </Alert>

      <DataState
        isPending={wallet.isPending}
        isError={wallet.isError}
        error={wallet.error}
        onRetry={() => void wallet.refetch()}
      >
        {wallet.data ? (
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, md: 6 }}>
              {/* Shown first and largest: this is the figure that decides
                  whether the next call succeeds. */}
              <Figure
                label="قابل استفاده"
                amountIrr={wallet.data.available_balance_irr}
                hint="آنچه درخواست‌های بعدی شما از آن پرداخت می‌شود."
                emphasis
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <Figure
                label="رزروشده"
                amountIrr={wallet.data.held_balance_irr}
                hint="رزروشده برای درخواست‌های در جریان. در صورت ناموفق‌بودن آزاد می‌شود."
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <Figure
                label="موجودی ثبت‌شده"
                amountIrr={wallet.data.posted_balance_irr}
                hint="مجموع مبالغ واریزی، منهای مجموع مبالغ کسرشده."
              />
            </Grid>
          </Grid>
        ) : null}
      </DataState>

      <Typography variant="h2" sx={{ mt: 4, mb: 2 }}>
        گردش مالی
      </Typography>
      <DataState
        isPending={ledger.isPending}
        isError={ledger.isError}
        error={ledger.error}
        isEmpty={ledger.data?.results.length === 0}
        emptyMessage="هنوز تراکنشی برای این حساب ثبت نشده است. شارژ حساب از سمت ما انجام می‌شود و پس از ثبت، همین‌جا دیده می‌شود."
        onRetry={() => void ledger.refetch()}
      >
        <TableContainer component={Paper} variant="outlined" sx={{ overflowX: 'auto' }}>
          <Table size="small" aria-label="گردش مالی">
            <TableHead>
              <TableRow>
                <TableCell>زمان</TableCell>
                <TableCell>نوع</TableCell>
                <TableCell>شرح</TableCell>
                <TableCell>درخواست مرتبط</TableCell>
                <TableCell align="right">مبلغ</TableCell>
                <TableCell align="right">موجودی پس از آن</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {ledger.data?.results.map((entry) => (
                <TableRow key={entry.id} hover>
                  <TableCell>{formatDateTime(entry.created_at)}</TableCell>
                  <TableCell>{ledgerEntryLabel(entry.entry_type)}</TableCell>
                  <TableCell>{entry.description || UNSET.amount}</TableCell>
                  {/* What turns a list of amounts into an explanation: which
                      call this money moved for. Blank on a recharge and on an
                      operator adjustment, which is a real difference and not a
                      missing value. */}
                  <TableCell>
                    {entry.request_id ? (
                      <CopyableId value={entry.request_id} visible={8} label="شناسهٔ پیگیری" />
                    ) : (
                      UNSET.amount
                    )}
                  </TableCell>
                  <TableCell align="right">
                    <Movement amountIrr={entry.amount_irr} />
                  </TableCell>
                  <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>
                    {formatIrr(entry.balance_after_irr)}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </DataState>
    </Box>
  )
}
